import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import {
    useReferralData,
    useRegenerateReferralCode,
    useApplyReferralCode,
} from '../hooks/queries/index.js';
import { createLogger } from '../utils/logger.js';
import {
    Card,
    Text,
    Heading,
    Button,
    Badge,
    Icon,
    Alert,
    Spinner
} from './ui/index.jsx';

const log = createLogger('ReferralSection');

/**
 * Referral Section Component v2
 * Shows user's permanent referral code with usage stats
 * Supports code regeneration when maxed out
 * Now uses TanStack Query for data fetching and caching
 */
export default function ReferralSection({ pricingConfig }) {
    const { updateUserCredits } = useAuth();

    // TanStack Query hooks
    const {
        data: referralQueryData,
        isLoading: loading,
        error: queryError,
        refetch: refetchReferralData
    } = useReferralData();

    const regenerateMutation = useRegenerateReferralCode();
    const applyMutation = useApplyReferralCode();

    // Local UI state
    const [copied, setCopied] = useState(false);
    const [applyCode, setApplyCode] = useState('');
    const [applyResult, setApplyResult] = useState(null);

    // Extract data from query result
    const referralData = referralQueryData?.code;
    const stats = referralQueryData?.stats?.stats;
    const history = referralQueryData?.history?.history || [];
    const error = queryError?.message || null;

    // Get referral bonus info from pricing config
    const referralBonus = pricingConfig?.bonuses?.referral || {
        credits: 8,
        architectures: 1,
        description: 'Referral bonus - 1 architecture for both parties'
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            log.error('Failed to copy:', err);
        }
    };

    const shareReferral = () => {
        const shareUrl = `${window.location.origin}?ref=${referralData?.referralCode}`;
        const shareText = `Try The AI Architect - get ${referralBonus.architectures} free architecture${referralBonus.architectures > 1 ? 's' : ''} when you sign up with my referral code!`;

        if (navigator.share) {
            navigator.share({
                title: 'The AI Architect',
                text: shareText,
                url: shareUrl
            }).catch(() => {
                copyToClipboard(shareUrl);
            });
        } else {
            copyToClipboard(shareUrl);
        }
    };

    const handleRegenerateCode = async () => {
        if (!confirm('This will invalidate your current code. Anyone with your old link won\'t be able to use it. Continue?')) {
            return;
        }

        regenerateMutation.mutate(undefined, {
            onError: (err) => {
                window.showToast?.('Failed to regenerate code', 'error');
            }
        });
    };

    const handleApplyCode = async () => {
        if (!applyCode.trim()) return;

        setApplyResult(null);

        applyMutation.mutate(applyCode.trim(), {
            onSuccess: (response) => {
                if (response.success) {
                    // Update credits immediately in AuthContext for UI sync
                    if (response.newBalance !== undefined) {
                        updateUserCredits(response.newBalance);
                    }
                    setApplyResult({
                        type: 'success',
                        message: `Referral code applied! You received ${referralBonus.credits} credits (${referralBonus.architectures} architecture${referralBonus.architectures > 1 ? 's' : ''}).`
                    });
                    setApplyCode('');
                } else {
                    setApplyResult({
                        type: 'error',
                        message: response.error || 'Failed to apply referral code'
                    });
                }
            },
            onError: (err) => {
                setApplyResult({
                    type: 'error',
                    message: err.message || 'Failed to apply referral code'
                });
            }
        });
    };

    // Derived state for loading
    const regenerating = regenerateMutation.isPending;
    const applyLoading = applyMutation.isPending;

    if (loading) {
        return (
            <Card className="p-6">
                <div className="flex items-center justify-center py-12">
                    <Spinner size="lg" />
                    <Text className="ml-3">Loading referral data...</Text>
                </div>
            </Card>
        );
    }

    const codeStats = referralData?.stats;
    const isMaxedOut = referralData?.isMaxedOut;
    const isExpired = referralData?.isExpired;
    const canShare = !isMaxedOut && !isExpired;

    return (
        <div className="space-y-6">
            {/* Your Referral Code */}
            <Card className="p-6">
                <Heading level={2} className="mb-4 flex items-center">
                    <Icon name="gift" size="md" className="mr-2 text-[#7B61FF]" />
                    Invite Friends, Earn Credits
                </Heading>

                <Text variant="secondary" className="mb-6">
                    Share your referral code and both you and your friend will receive{' '}
                    <span className="text-[#7B61FF] font-semibold">{referralBonus.credits} credits</span>{' '}
                    ({referralBonus.architectures} free architecture{referralBonus.architectures > 1 ? 's' : ''}) each!
                </Text>

                {referralData?.referralCode ? (
                    <div className="space-y-4">
                        {/* Status Badges */}
                        {(isMaxedOut || isExpired) && (
                            <Alert variant="warning" className="mb-4">
                                <div className="flex items-center justify-between">
                                    <Text>
                                        {isMaxedOut ? 'Your code has reached maximum uses.' : 'Your code has expired.'}
                                        {' '}Generate a new one to continue referring friends.
                                    </Text>
                                </div>
                            </Alert>
                        )}

                        {/* Referral Code Display */}
                        <div className={`bg-black/20 border rounded-xl p-4 ${isMaxedOut || isExpired ? 'border-yellow-500/30' : 'border-white/10'}`}>
                            <div className="flex items-center justify-between mb-2">
                                <Text size="sm" variant="secondary">Your Referral Code</Text>
                                {codeStats && (
                                    <Badge variant={codeStats.remainingUses > 10 ? 'success' : codeStats.remainingUses > 0 ? 'warning' : 'error'} size="xs">
                                        {codeStats.currentUses} / {codeStats.maxUses} uses
                                    </Badge>
                                )}
                            </div>
                            <div className="flex items-center gap-3">
                                <code className={`text-2xl font-mono font-bold tracking-wider ${isMaxedOut || isExpired ? 'text-gray-500' : 'text-[#7B61FF]'}`}>
                                    {referralData.referralCode}
                                </code>
                                {canShare && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => copyToClipboard(referralData.referralCode)}
                                        className="ml-auto"
                                    >
                                        <Icon name={copied ? 'check' : 'copy'} size="sm" className="mr-1" />
                                        {copied ? 'Copied!' : 'Copy'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                            {canShare ? (
                                <>
                                    <Button onClick={shareReferral} variant="primary">
                                        <Icon name="share" size="sm" className="mr-2" />
                                        Share Link
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={() => copyToClipboard(`${window.location.origin}?ref=${referralData.referralCode}`)}
                                    >
                                        <Icon name="link" size="sm" className="mr-2" />
                                        Copy Link
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    onClick={handleRegenerateCode}
                                    variant="primary"
                                    disabled={regenerating}
                                >
                                    {regenerating ? (
                                        <><Spinner size="sm" className="mr-2" /> Generating...</>
                                    ) : (
                                        <><Icon name="refresh" size="sm" className="mr-2" /> Generate New Code</>
                                    )}
                                </Button>
                            )}
                        </div>

                        {/* Progress Bar */}
                        {codeStats && codeStats.maxUses > 0 && (
                            <div className="mt-4">
                                <div className="flex justify-between text-xs mb-1">
                                    <Text variant="muted">{codeStats.remainingUses} uses remaining</Text>
                                    <Text variant="muted">{Math.round((codeStats.currentUses / codeStats.maxUses) * 100)}%</Text>
                                </div>
                                <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all ${codeStats.remainingUses > 10 ? 'bg-green-500' : codeStats.remainingUses > 0 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                        style={{ width: `${(codeStats.currentUses / codeStats.maxUses) * 100}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <Alert variant="warning">
                        <Text>Unable to generate referral code. Please try again later.</Text>
                    </Alert>
                )}
            </Card>

            {/* Referral Stats */}
            {stats && (
                <Card className="p-6">
                    <Heading level={3} className="mb-4">Your Referral Stats</Heading>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="bg-black/20 rounded-xl p-4 text-center">
                            <Text size="3xl" weight="bold" className="text-[#7B61FF]">
                                {stats.totalReferrals || 0}
                            </Text>
                            <Text size="sm" variant="secondary">Friends Referred</Text>
                        </div>
                        <div className="bg-black/20 rounded-xl p-4 text-center">
                            <Text size="3xl" weight="bold" className="text-green-400">
                                {stats.totalCreditsEarned || 0}
                            </Text>
                            <Text size="sm" variant="secondary">Credits Earned</Text>
                        </div>
                        <div className="bg-black/20 rounded-xl p-4 text-center">
                            <Text size="3xl" weight="bold" className="text-blue-400">
                                {Math.floor((stats.totalCreditsEarned || 0) / 8)}
                            </Text>
                            <Text size="sm" variant="secondary">Free Architectures</Text>
                        </div>
                    </div>
                    {stats.referredBy && (
                        <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                            <Text size="sm" className="text-green-400">
                                You were referred by a friend and received {referralBonus.credits} bonus credits!
                            </Text>
                        </div>
                    )}
                </Card>
            )}

            {/* Apply Referral Code */}
            {!stats?.referredBy && (
                <Card className="p-6">
                    <Heading level={3} className="mb-4">Have a Referral Code?</Heading>
                    <Text variant="secondary" className="mb-4">
                        Enter a friend's referral code to get {referralBonus.credits} bonus credits.
                    </Text>

                    {applyResult && (
                        <Alert
                            variant={applyResult.type === 'success' ? 'success' : 'error'}
                            className="mb-4"
                        >
                            <Text>{applyResult.message}</Text>
                        </Alert>
                    )}

                    <div className="flex gap-3">
                        <input
                            type="text"
                            value={applyCode}
                            onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                            placeholder="Enter referral code"
                            className="flex-1 px-4 py-3 rounded-xl bg-black/20 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#7B61FF]/50 focus:bg-black/40 transition-all font-mono tracking-wider"
                            maxLength={20}
                        />
                        <Button
                            onClick={handleApplyCode}
                            disabled={!applyCode.trim() || applyLoading}
                            variant="primary"
                        >
                            {applyLoading ? <Spinner size="sm" /> : 'Apply'}
                        </Button>
                    </div>
                </Card>
            )}

            {/* Referral History */}
            {history.length > 0 && (
                <Card className="p-6">
                    <Heading level={3} className="mb-4">Referral History</Heading>
                    <div className="space-y-3">
                        {history.map((item) => (
                            <div
                                key={item.id}
                                className="flex items-center justify-between p-4 rounded-xl bg-black/20 border border-white/5"
                            >
                                <div>
                                    <Text weight="medium" className="flex items-center">
                                        <Icon name="user" size="xs" className="mr-2 text-[#7B61FF]" />
                                        {item.referee_email ? item.referee_email.split('@')[0] + '@...' : 'Friend'}
                                    </Text>
                                    <Text size="xs" variant="muted">
                                        {new Date(item.created_at).toLocaleDateString()}
                                    </Text>
                                </div>
                                <Text className="text-green-400 font-medium">
                                    +{item.referrer_credits_awarded} credits
                                </Text>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {error && (
                <Alert variant="error">
                    <Text>{error}</Text>
                    <Button onClick={() => refetchReferralData()} variant="ghost" size="sm" className="mt-2">
                        Try Again
                    </Button>
                </Alert>
            )}
        </div>
    );
}
